import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useFavorites();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileSearch(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/category/movie?keyword=${encodeURIComponent(kw)}&scope=all`);
    setKeyword("");
    setMobileSearch(false);
  };

  const solid = scrolled || !isHome || mobileOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 w-full transition-colors duration-300 ${
        solid
          ? "border-b border-background-800 bg-background-950/95 backdrop-blur"
          : "border-b border-transparent bg-gradient-to-b from-background-950/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-6 lg:px-10">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-movie-2-fill text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-50 md:text-xl">
              麻豆<span className="text-primary-500">免费视频</span>
            </span>
            <span className="hidden text-[10px] tracking-[0.25em] text-foreground-500 md:block">
              MADOU VIDEO
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex md:pl-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-500/15 text-primary-400"
                    : "text-foreground-300 hover:bg-background-800 hover:text-foreground-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden items-center rounded-md border border-background-700 bg-background-900/80 px-3 py-1.5 transition-colors focus-within:border-primary-500 md:flex"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称…"
              className="w-32 bg-transparent px-2 py-0.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-600 lg:w-44"
            />
          </form>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => setMobileSearch((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800 md:hidden"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <Link
            to="/favorites"
            aria-label="我的收藏"
            className="relative flex h-9 w-9 items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800"
          >
            <i className="ri-heart-3-line text-lg"></i>
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-950">
                {count}
              </span>
            )}
          </Link>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800 md:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileSearch && (
        <div className="border-t border-background-800 px-4 py-3 md:hidden">
          <form
            onSubmit={submitSearch}
            className="flex items-center rounded-md border border-background-700 bg-background-900 px-3 py-2"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入影视名称关键词"
              className="w-full bg-transparent px-2 text-sm text-foreground-100 outline-none placeholder:text-foreground-600"
            />
            <button type="submit" className="whitespace-nowrap text-sm font-medium text-primary-400">
              搜索
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-background-800 bg-background-950/98 px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-500/15 text-primary-400"
                      : "text-foreground-200 hover:bg-background-800"
                  }`
                }
              >
                <i className="ri-arrow-right-s-line text-base"></i>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}